<template>
  <div class="ds-page" v-if="!$store.getters.isForbidden">
    <div class="page-header d-print-none">
      <div class="row align-items-center">
        <div class="col">
          <div class="page-pretitle">Company WPS</div>
          <h2 class="page-title">WPS List</h2>
          <p class="page-subtitle">Manage and track company Wage Payment Service submissions</p>
        </div>
        <div class="col-auto ms-auto">
          <button v-if="canDownload" @click="downloadHandler" class="ds-btn ds-btn--blue mr-2" :disabled="!list.length">
            <font-awesome-icon icon="download" />
            Download
          </button>
          <button v-if="canCreateWps" @click="handlerBtnCreateWps" class="ds-btn ds-btn--blue">
            <font-awesome-icon icon="hand-holding-usd" />
            Create WPS
          </button>
        </div>
      </div>
    </div>

    <!-- Filter Card -->
    <div class="ds-card">
      <div class="ds-card__body">
        <div class="wps-filter-header">
          <span class="ds-filters__label">Filters</span>
          <span class="ds-filters__hint">Default view starts from ops approval queue</span>
        </div>

        <div class="wps-filter-two-col">
          <!-- Left: Filter & Sort -->
          <div class="wps-filter-left">
            <div class="wps-filter-grid-left">
              <div class="ds-field wps-field-date">
                <label class="ds-field__label">Request Date</label>
                <el-date-picker
                  v-model="filters.date_filter"
                  type="daterange"
                  size="small"
                  :picker-options="datePickerOptions"
                  class="w-100"
                  range-separator="→"
                  value-format="yyyy-MM-dd"
                  start-placeholder="Start"
                  end-placeholder="End"
                />
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Filter By</label>
                <el-select
                  v-model="filters.filter_by"
                  size="small"
                  class="w-100"
                  placeholder="Select filter"
                  @change="handleFilterByChange"
                >
                  <el-option v-for="item in filterByOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Filter Value</label>
                <el-select v-model="filters.filter_value" size="small" class="w-100" placeholder="Select value">
                  <el-option
                    v-for="item in selectedFilterValueOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Sort By</label>
                <el-select v-model="filters.sort_by" size="small" class="w-100" placeholder="Sort by">
                  <el-option v-for="item in sortByOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Order</label>
                <el-select v-model="filters.sort_type" size="small" class="w-100" placeholder="Order">
                  <el-option v-for="item in sortTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </div>
            </div>
          </div>

          <!-- Separator -->
          <div class="wps-filter-sep"></div>

          <!-- Right: Search & Actions -->
          <div class="wps-filter-right">
            <div class="ds-field">
              <label class="ds-field__label">Search By</label>
              <el-select
                v-model="filters.search_by"
                size="small"
                class="w-100"
                placeholder="Select search"
                @change="handleSearchByChange"
              >
                <el-option v-for="item in searchByOptions" :key="item.value" :label="item.label" :value="item.value" />
              </el-select>
            </div>

            <div class="ds-field">
              <label class="ds-field__label">Search Value</label>
              <el-input
                v-model="filters.search_value"
                clearable
                size="small"
                :placeholder="searchPlaceholder"
                @clear="searchHandler"
              />
            </div>

            <div class="wps-filter-actions">
              <button @click="resetHandler" class="ds-btn ds-btn--secondary ds-btn--sm">Reset</button>
              <button @click="searchHandler" class="ds-btn ds-btn--blue ds-btn--sm">
                <font-awesome-icon icon="search" />
                Search
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Table Card -->
    <div class="ds-card">
      <div class="ds-table-wrap">
        <table class="ds-table" v-loading="is_loading">
          <thead>
            <tr>
              <th>Request Date</th>
              <th>Company</th>
              <th>Client</th>
              <th class="text-right">Employees</th>
              <th class="text-right">Total Amount</th>
              <th class="text-right">Total Salary</th>
              <th class="text-right">Admin Fee</th>
              <th>Requested By</th>
              <th>Status</th>
              <th>Disburse</th>
              <th class="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in list" :key="i">
              <td>
                <span class="ds-text-sm ds-text-muted">{{ formatDate(item.created_at) }}</span>
              </td>
              <td>
                <span class="ds-text-bold">{{ item.company?.company_name || "—" }}</span>
              </td>
              <td>{{ item.company_client?.name || "—" }}</td>
              <td class="text-right">
                <span class="ds-badge">{{ item.total_employee }}</span>
              </td>
              <td class="text-right">
                <span class="ds-amount">{{ formatCurrency(item.total_amount) }}</span>
              </td>
              <td class="text-right">
                <span class="ds-amount">{{ formatCurrency(item.total_salary) }}</span>
              </td>
              <td class="text-right">
                <span class="ds-amount--muted">{{ formatCurrency(item.admin_fee) }}</span>
              </td>
              <td>
                <div class="ds-user-cell">
                  <div class="ds-avatar" :class="dsAvatarVariant(item?.requested_by?.full_name)">
                    {{ dsGetInitials(item?.requested_by?.full_name) }}
                  </div>
                  <div>
                    <div class="ds-user-cell__name">{{ item?.requested_by?.full_name || "—" }}</div>
                    <div class="ds-user-cell__sub">{{ item?.requested_by?.email || "" }}</div>
                  </div>
                </div>
              </td>
              <td>
                <span class="ds-pill" :class="dsStatusClass(item.status)">
                  {{ dsStatusLabel(item.status) }}
                </span>
              </td>
              <td>
                <span class="ds-pill" :class="dsStatusClass(item.disburse_status)">
                  {{ dsStatusLabel(item.disburse_status) }}
                </span>
              </td>
              <td class="text-center">
                <div class="ds-row-actions">
                  <el-tooltip v-if="isChecker && canApprove(item)" content="Approve request" placement="top">
                    <el-button @click="handlerBtnApprove(item)" type="success" size="mini" class="wps-action-approve" circle>
                      <font-awesome-icon icon="check" />
                    </el-button>
                  </el-tooltip>
                  <el-tooltip v-if="isChecker && canApprove(item)" content="Reject request" placement="top">
                    <el-button @click="handlerBtnReject(item)" type="danger" size="mini" class="wps-action-reject" circle>
                      <font-awesome-icon icon="times" />
                    </el-button>
                  </el-tooltip>
                  <el-tooltip v-if="canCancel(item)" content="Cancel request" placement="top">
                    <el-button @click="handlerBtnCancel(item)" type="warning" size="mini" class="wps-action-cancel" circle>
                      <font-awesome-icon icon="ban" />
                    </el-button>
                  </el-tooltip>
                  <el-tooltip content="View request detail" placement="top">
                    <el-button size="mini" @click="handlerBtnView(item)" class="wps-action-view" circle>
                      <font-awesome-icon icon="address-book" />
                    </el-button>
                  </el-tooltip>
                  <el-tooltip content="View activity log" placement="top">
                    <el-button size="mini" @click="handlerBtnLog(item)" class="wps-action-log" circle>
                      <font-awesome-icon icon="history" />
                    </el-button>
                  </el-tooltip>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <el-empty v-if="!total_rows" description="No WPS requests found" :image="emptyStateImage" class="ds-empty" />
      </div>

      <pagination :page="page" :per_page="per_page" :list="list" :total_rows="total_rows" @pageChange="pageChangeHandler" />
    </div>

    <!-- Approve Modal -->
    <b-modal size="md" v-model="show.modal_wps" :title="approvalModalTitle" hide-footer>
      <div class="wps-modal-body">
        <el-descriptions :loading="loading.modal_wps" title="" :column="1" border>
          <el-descriptions-item>
            <template slot="label"> <i class="el-icon-office-building"></i> Request Number </template>
            <strong>{{ selected_wps.request_number }}</strong>
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label"> <font-awesome-icon icon="users" size="sm" /> Total Recipients </template>
            {{ selected_wps.total_employee }} Employees
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label"> <font-awesome-icon icon="money-bill-alt" size="sm" /> Total WPS Amount </template>
            <strong>{{ formatCurrency(selected_wps.total_amount) }}</strong>
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label"> <font-awesome-icon icon="sticky-note" size="sm" /> Notes </template>
            <el-input v-model="approval.notes" placeholder="Add notes..." type="text" size="small" />
          </el-descriptions-item>
        </el-descriptions>

        <div class="wps-modal-actions">
          <button @click="show.modal_wps = false" class="ds-btn ds-btn--secondary ds-btn--sm">Cancel</button>
          <button @click="submitApproval" :class="approvalModalButtonClass" class="ds-btn ds-btn--sm">
            <font-awesome-icon :icon="approvalModalIcon" />
            {{ approvalModalButtonText }}
          </button>
        </div>
      </div>
    </b-modal>
    <b-modal size="xl" v-model="show.modal_log" title="WPS Activity Log" hide-footer body-class="ds-timeline-modal">
      <div class="ds-timeline-shell">
        <!-- Header Card -->
        <div class="ds-timeline-header-card">
          <div class="ds-timeline-header-left">
            <div class="ds-timeline-eyebrow">
              <font-awesome-icon icon="history" class="ds-timeline-eyebrow__icon" />
              Trace Timeline
            </div>
            <div class="ds-timeline-title">{{ selected_log.request_number || "WPS Request" }}</div>
            <div class="ds-timeline-subtitle">
              <span class="ds-timeline-meta-chip">
                <font-awesome-icon icon="building" class="ds-timeline-meta-chip__icon" />
                {{ selected_log.company_name || "—" }}
              </span>
              <span class="ds-timeline-meta-sep">›</span>
              <span class="ds-timeline-meta-chip">
                <font-awesome-icon icon="user-tie" class="ds-timeline-meta-chip__icon" />
                {{ selected_log.company_client_name || "—" }}
              </span>
            </div>
          </div>
          <div class="ds-timeline-header-right">
            <span class="ds-pill" :class="dsStatusClass(selected_log.current_status)">
              {{ dsStatusLabel(selected_log.current_status) }}
            </span>
            <div class="ds-timeline-entry-count" v-if="!loading.modal_log && logEntries.length">
              {{ logEntries.length }} event{{ logEntries.length !== 1 ? "s" : "" }}
            </div>
          </div>
        </div>

        <!-- Skeleton Loading -->
        <div v-if="loading.modal_log" class="ds-timeline-loading">
          <div v-for="i in 4" :key="i" class="ds-timeline-skeleton">
            <div class="ds-timeline-skeleton__rail">
              <div class="ds-timeline-skeleton__dot"></div>
              <div class="ds-timeline-skeleton__line-v"></div>
            </div>
            <div class="ds-timeline-skeleton__body">
              <div class="ds-timeline-skeleton__line w-30"></div>
              <div class="ds-timeline-skeleton__line w-60" style="margin-top: 6px"></div>
              <div class="ds-timeline-skeleton__line w-45" style="margin-top: 6px"></div>
            </div>
          </div>
        </div>

        <!-- Empty -->
        <el-empty
          v-else-if="!logEntries.length"
          description="No activity recorded yet"
          :image="emptyStateImage"
          class="ds-empty"
        />

        <!-- Timeline -->
        <div v-else class="ds-timeline-timeline">
          <div
            v-for="(entry, idx) in logEntries"
            :key="entry.id"
            class="ds-timeline-item"
            :class="{ 'ds-timeline-item--last': idx === logEntries.length - 1 }"
          >
            <!-- Rail -->
            <div class="ds-timeline-item__rail">
              <div class="ds-timeline-item__dot" :class="entry.toneClass">
                <font-awesome-icon :icon="entry.icon" />
              </div>
              <div class="ds-timeline-item__connector" v-if="idx < logEntries.length - 1"></div>
            </div>

            <!-- Card -->
            <div class="ds-timeline-item__card" :class="entry.toneClass + '--card'">
              <!-- Top row: action + timestamp -->
              <div class="ds-timeline-card__header">
                <div class="ds-timeline-card__header-left">
                  <span class="ds-timeline-item__title">{{ entry.actionLabel }}</span>
                  <span class="ds-timeline-item__time">
                    <font-awesome-icon icon="clock" class="ds-timeline-time-icon" />
                    {{ entry.createdAtLabel }}
                  </span>
                </div>
                <!-- Status transition pill -->
                <div class="ds-timeline-transition">
                  <span class="ds-timeline-transition__badge ds-timeline-transition__badge--from">
                    {{ entry.fromStatusLabel }}
                  </span>
                  <div class="ds-timeline-transition__arrow-wrap">
                    <font-awesome-icon icon="arrow-right" />
                  </div>
                  <span
                    class="ds-timeline-transition__badge ds-timeline-transition__badge--to"
                    :class="entry.toneClass + '--badge'"
                  >
                    {{ entry.toStatusLabel }}
                  </span>
                </div>
              </div>

              <!-- Divider -->
              <div class="ds-timeline-card__divider"></div>

              <!-- Actor -->
              <div class="ds-timeline-item__actor">
                <div class="ds-avatar ds-avatar--sm" :class="dsAvatarVariant(entry.actorName)">
                  {{ dsGetInitials(entry.actorName) }}
                </div>
                <div class="ds-timeline-actor__info">
                  <span class="ds-timeline-actor__name">{{ entry.actorName }}</span>
                  <span class="ds-timeline-actor__meta">
                    {{ entry.actorEmail }}
                    <template v-if="entry.actorRole">
                      <span class="ds-timeline-actor__sep">·</span>
                      <span class="ds-timeline-actor__role">{{ entry.actorRole }}</span>
                    </template>
                  </span>
                </div>
              </div>

              <!-- Notes -->
              <div v-if="entry.notes" class="ds-timeline-notes">
                <span class="ds-timeline-notes__label"> <font-awesome-icon icon="sticky-note" /> Notes </span>
                <p class="ds-timeline-notes__value">{{ entry.notes }}</p>
              </div>

              <!-- Metadata -->
              <div v-if="entry.metadataText" class="ds-timeline-metadata">
                <span class="ds-timeline-notes__label"> <font-awesome-icon icon="code" /> Metadata </span>
                <pre class="ds-timeline-metadata__value">{{ entry.metadataText }}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
    <create-wps-request
      v-if="showModalWps"
      :is-shown="showModalWps"
      :mode="formMode"
      :client-data="selectedWps"
      @onHide="
        showModalWps = false;
        getList();
      "
    />
  </div>
</template>

<script>
import moment from "moment";
import { designSystemMixin } from "@/mixins/designSystemMixin";
import {
  CANCEL_WPS,
  GET_WPS_LIST,
  INQUIRY_APPROVAL_WPS,
  SUBMIT_APPROVAL_CRM_WPS,
  SUBMIT_REJECT_CRM_WPS,
  WPS_LOG,
} from "@/store/modules/wps";
import emptyStateImage from "@/assets/images/empty-state.png";
import popupErrorMessages from "@/library/popup-error-messages";
import Pagination from "@/ui/components/Pagination.vue";
import CreateWpsRequest from "../wps/CreateWpsRequest.vue";

const DEFAULT_WORKFLOW_STATUS = "";

export default {
  name: "CompaniesWps",
  mixins: [designSystemMixin],
  components: {
    Pagination,
    CreateWpsRequest,
  },
  metaInfo: {
    title: "Wage Payment Service",
  },
  computed: {
    currentRole() {
      return this.$store.getters.current_role;
    },
    userProfile() {
      return this.$store.getters.currentMyProfile;
    },
    isMaker() {
      return this.userProfile.is_maker || this.currentRole.page_name === "*";
    },
    isChecker() {
      return this.userProfile.is_checker || this.currentRole.page_name === "*";
    },
    canCreateWps() {
      return (Boolean(this.currentRole?.can_create) || this.currentRole.page_name === "*") && this.isMaker;
    },
    canDownload() {
      return Boolean(this.currentRole?.can_download) || this.currentRole.page_name === "*";
    },
    canEditWps() {
      return Boolean(this.currentRole?.can_edit) || this.currentRole.page_name === "*";
    },
    approvalModalTitle() {
      if (this.approvalAction === "reject") return "Reject WPS Request";
      if (this.approvalAction === "cancel") return "Cancel WPS Request";
      return "Approve WPS Request";
    },
    approvalModalButtonText() {
      if (this.approvalAction === "reject") return "Submit Rejection";
      if (this.approvalAction === "cancel") return "Submit Cancellation";
      return "Submit Approval";
    },
    approvalModalButtonClass() {
      if (this.approvalAction === "reject") return "ds-btn--danger";
      if (this.approvalAction === "cancel") return "ds-btn--warning";
      return "ds-btn--primary";
    },
    approvalModalIcon() {
      if (this.approvalAction === "reject") return "times";
      if (this.approvalAction === "cancel") return "ban";
      return "check";
    },
    selectedFilterValueOptions() {
      return this.filters.filter_by === "disburse_status" ? this.disburseStatusOptions : this.statusOptions;
    },
    searchPlaceholder() {
      return this.filters.search_by === "company_client_name" ? "Input company client name" : "Input company name";
    },
  },
  data() {
    return {
      emptyStateImage,
      approval: {
        notes: "",
      },
      approvalAction: "approve",
      filters: {
        date_filter: [moment().startOf("month").format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
        filter_by: "status",
        filter_value: DEFAULT_WORKFLOW_STATUS,
        search_by: "company_name",
        search_value: "",
        sort_by: "created_at",
        sort_type: "desc",
      },
      datePickerOptions: {
        disabledDate: (date) => moment(date).isAfter(moment(), "days"),
      },
      filterByOptions: [
        { value: "status", label: "Status" },
        { value: "disburse_status", label: "Disburse Status" },
      ],
      statusOptions: [
        { value: "", label: "All" },
        { value: "WAITING_APPROVAL_OPS", label: "Waiting Approval Ops" },
        { value: "WAITING_APPROVAL_FINANCE", label: "Waiting Approval Finance" },
        { value: "APPROVED", label: "Approved" },
        { value: "REJECTED", label: "Rejected" },
      ],
      disburseStatusOptions: [
        { value: "", label: "All" },
        { value: "WAITING_APPROVAL", label: "Waiting Approval" },
        { value: "WAITING_DISBURSEMENT", label: "Waiting Disbursement" },
        { value: "PROCESS_DISBURSEMENT", label: "Processing Disbursement" },
        { value: "COMPLETED", label: "Completed" },
      ],
      searchByOptions: [
        { value: "company_name", label: "Company" },
        { value: "company_client_name", label: "Company Client" },
      ],
      sortByOptions: [
        { value: "created_at", label: "Request Date" },
        { value: "company_name", label: "Company Name" },
        { value: "total_amount", label: "Total Amount" },
        { value: "total_employee", label: "Total Employee" },
      ],
      sortTypeOptions: [
        { value: "desc", label: "Descending" },
        { value: "asc", label: "Ascending" },
      ],
      wps_approval_id: "",
      showModalWps: false,
      formMode: "create",
      selectedWps: null,
      is_loading: false,
      total_rows: 0,
      page: 1,
      per_page: 10,
      list: [],
      show: {
        modal_wps: false,
        modal_log: false,
      },
      loading: {
        modal_wps: false,
        modal_log: false,
      },
      selected_wps: {},
      selected_log: {
        request_number: "",
        company_name: "",
        company_client_name: "",
        current_status: "",
      },
      logEntries: [],
    };
  },
  async mounted() {
    this.is_loading = true;
    await this.getList();
    this.is_loading = false;
  },
  methods: {
    formatDate(value) {
      return value ? moment(value).format("DD MMM YYYY, HH:mm") : "-";
    },
    formatCurrency(value) {
      return `Rp. ${Number(value || 0).toLocaleString("id-ID")}`;
    },
    formatLogStatusLabel(status) {
      if (!status) return "Initial State";
      return this.dsStatusLabel(status);
    },
    getLogTone(action = "", toStatus = "") {
      const normalizedAction = String(action).toUpperCase();
      const normalizedStatus = String(toStatus).toUpperCase();

      if (normalizedAction.includes("REJECT") || normalizedStatus === "REJECTED" || normalizedStatus === "FAILED") {
        return { toneClass: "ds-timeline-item__dot--danger", icon: "times" };
      }
      if (normalizedAction.includes("PROCESS")) {
        return { toneClass: "ds-timeline-item__dot--info", icon: "spinner" };
      }
      if (normalizedAction.includes("COMPLETE") || normalizedStatus === "COMPLETED" || normalizedStatus === "SUCCESS") {
        return { toneClass: "ds-timeline-item__dot--success", icon: "check" };
      }
      if (normalizedAction.includes("APPROVE")) {
        return { toneClass: "ds-timeline-item__dot--success", icon: "thumbs-up" };
      }
      return { toneClass: "ds-timeline-item__dot--neutral", icon: "history" };
    },
    normalizeLogEntry(entry) {
      const tone = this.getLogTone(entry.action, entry.to_status);
      return {
        ...entry,
        actionLabel: this.dsStatusLabel(entry.action),
        fromStatusLabel: this.formatLogStatusLabel(entry.from_status),
        toStatusLabel: this.formatLogStatusLabel(entry.to_status),
        createdAtLabel: this.formatDate(entry.created_at),
        actorName: entry.performed_by?.full_name || entry.performed_by?.name || "System",
        actorEmail: entry.performed_by?.email || "—",
        actorRole: entry.performed_by?.role_name || "",
        notes: entry.notes || "",
        metadataText: entry.metadata ? JSON.stringify(entry.metadata, null, 2) : "",
        toneClass: tone.toneClass,
        icon: tone.icon,
      };
    },
    normalizeFilterValue() {
      if (this.filters.filter_by !== "status") {
        return this.filters.filter_value;
      }

      const statusMap = {
        WAITING_APPROVAL_OPS: "WAITING_APPROVAL",
        WAITING_APPROVAL_FINANCE: "APPROVED",
      };

      return statusMap[this.filters.filter_value] || this.filters.filter_value;
    },
    buildQuery(extraParams = {}) {
      const query = {
        page: this.page,
        size: this.per_page,
        per_page: this.per_page,
        sort: this.filters.sort_type === "desc" ? `-${this.filters.sort_by}` : this.filters.sort_by,
        filter_date: "created_at",
        ...extraParams,
      };

      if (this.filters.filter_by && this.filters.filter_value) {
        query[this.filters.filter_by] = this.normalizeFilterValue();
      }

      if (this.filters.search_by && this.filters.search_value) {
        query.search = this.filters.search_by;
        query.search_value = this.filters.search_value;
      }

      if (Array.isArray(this.filters.date_filter) && this.filters.date_filter.length === 2) {
        query.start_date = this.filters.date_filter[0];
        query.end_date = this.filters.date_filter[1];
      }

      return query;
    },
    canApprove(item) {
      return this.canEditWps && item.status === "WAITING_APPROVAL" && item.disburse_status === "WAITING_APPROVAL";
    },
    canCancel(item) {
      return this.canEditWps && this.isMaker && item.status === "WAITING_APPROVAL" && item.disburse_status === "WAITING_APPROVAL";
    },
    handlerBtnCreateWps() {
      this.formMode = "create";
      this.selectedWps = null;
      this.showModalWps = true;
    },
    searchHandler() {
      this.page = 1;
      this.getList();
    },
    handleFilterByChange() {
      this.filters.filter_value = "";
    },
    handleSearchByChange() {
      this.filters.search_value = "";
    },
    resetHandler() {
      this.filters = {
        date_filter: [moment().startOf("month").format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
        filter_by: "status",
        filter_value: DEFAULT_WORKFLOW_STATUS,
        search_by: "company_name",
        search_value: "",
        sort_by: "created_at",
        sort_type: "desc",
      };
      this.page = 1;
      this.getList();
    },
    async downloadHandler() {
      this.is_loading = true;
      this.$parent.showLoader();
      try {
        const response = await this.$store.dispatch(
          GET_WPS_LIST,
          this.buildQuery({
            action: "download",
          })
        );
        const data = response?.data ?? response;

        this.$message({
          title: "Success",
          type: "success",
          message: "Success! Your CSV download will start shortly.",
        });

        this.$nextTick(() => {
          const anchor = document.createElement("a");
          anchor.href = `data:text/csv;charset=utf-8,${encodeURIComponent(data)}`;
          anchor.target = "_blank";
          anchor.download = "companies-wps-list.csv";
          anchor.click();
        });
      } finally {
        this.$parent.hideLoader();
        this.is_loading = false;
      }
    },
    async pageChangeHandler(page) {
      this.page = page;
      this.is_loading = true;
      await this.getList();
      this.is_loading = false;
    },
    async getList() {
      this.is_loading = true;
      this.$parent.showLoader();
      try {
        await this.$store.dispatch(GET_WPS_LIST, this.buildQuery());
        const { rows, count } = this.$store.getters.wps_list;
        this.total_rows = count || 0;
        this.list = Array.isArray(rows) ? rows : [];
      } finally {
        this.$parent.hideLoader();
        this.is_loading = false;
      }
    },
    async handlerBtnLog(item) {
      this.show.modal_log = true;
      this.loading.modal_log = true;
      this.logEntries = [];
      this.selected_log = {
        request_number: item.request_number || "",
        company_name: item.company?.company_name || "—",
        company_client_name: item.company_client?.name || "—",
        current_status: item.disburse_status || item.status || "",
      };

      try {
        const response = await this.$store.dispatch(WPS_LOG, {
          id: item.id,
          page: 1,
          per_page: 15,
          size: 15,
        });
        const rows = Array.isArray(response?.rows) ? response.rows : [];
        this.logEntries = rows.map((entry) => this.normalizeLogEntry(entry));
      } catch (err) {
        this.show.modal_log = false;
        if (err.response?.data) {
          popupErrorMessages(err.response.data);
        }
      } finally {
        this.loading.modal_log = false;
      }
    },
    handlerBtnApprove(item) {
      this.show.modal_wps = true;
      this.approvalAction = "approve";
      this.selected_wps = item;
      this.wps_approval_id = "";
      this.approval = {
        notes: "",
      };
      this.inquiry();
    },
    handlerBtnReject(item) {
      this.show.modal_wps = true;
      this.approvalAction = "reject";
      this.selected_wps = item;
      this.wps_approval_id = "";
      this.approval = {
        notes: "",
      };
      this.inquiry();
    },
    handlerBtnCancel(item) {
      this.show.modal_wps = true;
      this.approvalAction = "cancel";
      this.selected_wps = item;
      this.wps_approval_id = "";
      this.approval = {
        notes: "",
      };
    },
    async inquiry() {
      this.loading.modal_wps = true;
      try {
        const response = await this.$store.dispatch(INQUIRY_APPROVAL_WPS, {
          id: this.selected_wps.id,
          current_state: "WAITING_APPROVAL",
          approval_type: "batch",
          bank_code: [],
        });

        const inquiryResult = response?.data?.data?.rows || {};
        this.selected_wps.wps_approval_id = response?.data?.wps_approval_id || inquiryResult.wps_approval_id;
        this.wps_approval_id = response?.data?.wps_approval_id || inquiryResult.wps_approval_id || "";
      } catch (error) {
        console.error("Error during inquiry:", error);
      } finally {
        this.loading.modal_wps = false;
      }
    },
    async submitApproval() {
      if (this.approvalAction === "reject" && !this.approval.notes.trim()) {
        this.$message.error("Notes are required for rejection.");
        return;
      }

      let confirmMessage = "You will approve this WPS batch. You cannot undo this action. Continue?";
      let successMessage = "Approval success";

      if (this.approvalAction === "reject") {
        confirmMessage = "You will reject this WPS batch. You cannot undo this action. Continue?";
        successMessage = "Rejection success";
      } else if (this.approvalAction === "cancel") {
        confirmMessage = "You will cancel this WPS batch. You cannot undo this action. Continue?";
        successMessage = "Cancellation success";
      }

      this.$confirm(confirmMessage, "Confirmation", {
        type: "info",
        beforeClose: async (action, instance, done) => {
          if (action === "confirm") {
            instance.confirmButtonLoading = true;
            try {
              const payload = {
                id: this.selected_wps.id,
                notes: this.approval.notes,
                wps_approval_id: this.wps_approval_id,
              };
              if (this.approvalAction === "reject") {
                await this.$store.dispatch(SUBMIT_REJECT_CRM_WPS, payload);
              } else if (this.approvalAction === "cancel") {
                await this.$store.dispatch(CANCEL_WPS, {
                  id: this.selected_wps.id,
                  notes: this.approval.notes,
                });
              } else {
                await this.$store.dispatch(SUBMIT_APPROVAL_CRM_WPS, payload);
              }
              this.$message({
                title: "Success",
                type: "success",
                message: successMessage,
              });
              this.show.modal_wps = false;
              this.getList();
            } catch (err) {
              if (err.response?.data) {
                popupErrorMessages(err.response.data);
              }
            }
            instance.confirmButtonLoading = false;
          }
          done();
        },
      }).catch(() => {});
    },
    async handlerBtnView(item) {
      this.$router.push({
        name: "Companies WPS Detail",
        params: {
          id: item.id,
        },
        query: {
          request_number: item.request_number || "",
        },
      });
    },
  },
};
</script>

<style scoped>
/* ── Filter: 2-column layout */
.wps-filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.wps-filter-two-col {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0 20px;
  align-items: start;
}

/* Left: 2-col inner grid */
.wps-filter-grid-left {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 12px;
  align-items: end;
}

/* Date picker spans full width on the left */
.wps-field-date {
  grid-column: span 2;
}

/* Vertical separator */
.wps-filter-sep {
  width: 1px;
  background: rgba(101, 109, 119, 0.12);
  align-self: stretch;
  margin-top: 2px;
}

/* Right: stacked fields */
.wps-filter-right {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Actions pinned to bottom-right */
.wps-filter-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 4px;
}

/* ── Action buttons */
.wps-action-approve.el-button,
.wps-action-view.el-button,
.wps-action-log.el-button {
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  font-size: 11px !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
}
.wps-action-approve.el-button--success {
  background: rgba(47, 179, 68, 0.1) !important;
  border-color: rgba(47, 179, 68, 0.24) !important;
  color: #2fb344 !important;
}
.wps-action-approve.el-button--success:hover {
  background: rgba(47, 179, 68, 0.2) !important;
}
.wps-action-reject.el-button--danger {
  background: rgba(214, 57, 57, 0.1) !important;
  border-color: rgba(214, 57, 57, 0.24) !important;
  color: #d63939 !important;
}
.wps-action-reject.el-button--danger:hover {
  background: rgba(214, 57, 57, 0.18) !important;
}
.wps-action-cancel.el-button--warning {
  background: rgba(245, 159, 0, 0.12) !important;
  border-color: rgba(245, 159, 0, 0.24) !important;
  color: #c47b07 !important;
}
.wps-action-cancel.el-button--warning:hover {
  background: rgba(245, 159, 0, 0.2) !important;
}
.wps-action-view.el-button {
  background: #f4f6fa !important;
  border-color: rgba(101, 109, 119, 0.24) !important;
  color: #656d77 !important;
}
.wps-action-view.el-button:hover {
  background: rgba(101, 109, 119, 0.1) !important;
}
.wps-action-log.el-button {
  background: rgba(32, 107, 196, 0.08) !important;
  border-color: rgba(32, 107, 196, 0.22) !important;
  color: #206bc4 !important;
}
.wps-action-log.el-button:hover {
  background: rgba(32, 107, 196, 0.16) !important;
}

/* ── Modal  */
.wps-modal-body {
  padding: 4px 0;
}
.wps-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}
</style>
